import React, { useState } from "react";
import axios from "axios";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TablePagination,
  CircularProgress,
  Tooltip,
  TableSortLabel,
} from "@mui/material";
import {
  DisabledByDefault,
  DownloadForOffline,
  HourglassBottom,
  Preview,
} from "@mui/icons-material";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useSelector } from "react-redux";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import { API_ENDPOINT } from "../../../services/api/resources";
import classes from "./Invoice.module.css";
import SearchBar from "../../../components/common/SearchBar/SearchBar";
import { useVendorInvoicesOffline } from "../../../offline";

const normalizeApprovalState = (value, label = "") => {
  const numericStatus = Number(value);
  if (Number.isFinite(numericStatus)) {
    if (numericStatus === 1) return "APPROVED";
    if (numericStatus === 2) return "PENDING";
    if (numericStatus === 3) return "REJECTED";
  }

  const normalized = String(label || value || "").toLowerCase().trim();
  if (!normalized) return "";
  if (normalized.includes("reject") || normalized.includes("cancel"))
    return "REJECTED";
  if (normalized.includes("approve")) return "APPROVED";
  if (normalized.includes("pending") || normalized.includes("review"))
    return "PENDING";
  return "";
};

const getVendorMerchantState = (item) => {
  if (item?.workflow_state === "vendor_signature") return "PENDING";
  if (
    item?.workflow_state === "account_payable_approval" ||
    item?.workflow_state === "completed" ||
    Number(item?.vendor_approved) === 1 ||
    Number(item?.merchant_approved) === 1
  ) {
    return "APPROVED";
  }
  return normalizeApprovalState(item?.status, item?.status_label);
};

const isInvoiceOverallRejected = (item) => {
  const finance = normalizeApprovalState(item?.finance_approved);
  const account = normalizeApprovalState(item?.account_approved);
  const vendorMerchant = getVendorMerchantState(item);

  return (
    finance === "REJECTED" || account === "REJECTED" || vendorMerchant === "REJECTED"
  );
};

const isVendorMerchantApproved = (item) => {
  return getVendorMerchantState(item) === "APPROVED";
};

const ApproverInvoice = () => {
  const loginData = useSelector((state) => state.loginData.value);
  const orgToken =
    loginData?.organization_token ||
    loginData?.org_token ||
    loginData?.user?.token ||
    loginData?.user_token;
  const approverToken =
    loginData?.user?.user_token || loginData?.user_token || loginData?.user?.token;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogState, setDialogState] = useState({
    title: "Status",
    message: "",
    severity: "info",
  });
  const [invoiceOverrides, setInvoiceOverrides] = useState({});
  const [page, setPage] = useState(0); // 0-based for TablePagination
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [payeeStatusFilter, setPayeeStatusFilter] = useState("All");
  const [vendorStatusFilter, setVendorStatusFilter] = useState("All");
  const [sortConfig, setSortConfig] = useState({
    key: "created_on",
    direction: "desc",
  });
  const authToken = loginData?.access_token || loginData?.token || "";
  const isAccountPayableTeam = loginData?.user?.designation === "Account Payable";

  const {
    invoices: vendorinvoicedata,
    totalCount,
    loading,
    refetch,
  } = useVendorInvoicesOffline(
    authToken,
    orgToken,
    page + 1,
    rowsPerPage,
    sortConfig.key,
    sortConfig.direction
  );

  const showDialog = (message, title = "Status", severity = "info") => {
    setDialogState({ title, message, severity });
    setDialogOpen(true);
  };

  const getInvoiceToken = (item) => item?.invoice_token || "";

  const patchInvoiceRow = (invoiceToken, patch) => {
    if (!invoiceToken) return;
    setInvoiceOverrides((prev) => ({
      ...prev,
      [invoiceToken]: {
        ...(prev[invoiceToken] || {}),
        ...patch,
      },
    }));
  };

  const getInvoiceRow = (item) => ({
    ...item,
    ...(invoiceOverrides[getInvoiceToken(item)] || {}),
  });

  const getRoleLabel = (role) =>
    role === "account" ? "Account Payable" : "Business Finance";

  const isApprovalFinishedForRole = (item, role) => {
    const value =
      role === "account" ? item?.account_approved : item?.finance_approved;
    return Number(value) === 1 || Number(value) === 3;
  };

  const currentApprovalRole = isAccountPayableTeam ? "account" : "business";

  const isSelectableForCurrentRole = (item) =>
    !isInvoiceOverallRejected(item) &&
    !isApprovalFinishedForRole(item, currentApprovalRole) &&
    (!isAccountPayableTeam || isVendorMerchantApproved(item));

  const getApprovalPatch = (invoice, action, role, responseData = {}) => {
    const rejected = action === "reject";
    if (role === "account") {
      return rejected
        ? {
            account_approved: 3,
            status: 3,
            status_label: "Cancelled",
            workflow_state: responseData?.workflow_state || "rejected",
          }
        : {
            account_approved: 1,
            status: responseData?.status ?? 1,
            status_label: responseData?.status_label || "Approved",
            workflow_state: responseData?.workflow_state || "completed",
          };
    }

    return rejected
      ? {
          finance_approved: 3,
          status: 3,
          status_label: "Cancelled",
          workflow_state: responseData?.workflow_state || "rejected",
        }
      : {
          finance_approved: 1,
          is_bizfin_approved: 1,
          status: responseData?.status ?? invoice?.status ?? 2,
          status_label: responseData?.status_label || invoice?.status_label || "Pending",
          workflow_state: responseData?.workflow_state || "vendor_signature",
        };
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      const nextDirection =
        prev.key === key && prev.direction === "asc" ? "desc" : "asc";
      return { key, direction: nextDirection };
    });
    setPage(0);
    setSelectedInvoices([]);
  };

  const downloadPDF = (invoice) => {
    if (!invoice?.pdf_url) {
      showDialog("Invoice PDF not available");
      return;
    }
    const a = document.createElement("a");
    a.href = invoice.pdf_url;
    a.download = "Invoice.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const openPDFInNewTab = (invoice) => {
    if (!invoice?.pdf_url) {
      showDialog("Invoice PDF not available");
      return;
    }
    window.open(invoice.pdf_url, "_blank");
  };

  const handleApprove = async (invoice, action, role) => {
    const invoice_token = invoice?.invoice_token;
    if (!invoice_token) {
      showDialog("Invalid invoice.");
      return;
    }

    if (action === "accept" && isInvoiceOverallRejected(invoice)) {
      showDialog("This invoice is already rejected and cannot be approved.");
      return;
    }

    try {
      const payload = { invoice_token, action, approver_token: approverToken };
      if (role === "account") payload.is_account = true;
      if (role === "business") payload.is_business = true;

      const response = await axios.post(API_ENDPOINT.approve_vendor_invoice, payload);
      patchInvoiceRow(
        invoice_token,
        getApprovalPatch(invoice, action, role, response?.data),
      );

      showDialog(
        response?.data?.message ||
          `Invoice ${action === "accept" ? "approved" : "rejected"} by ${getRoleLabel(role)} Team.`,
        action === "accept" ? `${getRoleLabel(role)} Approval Complete` : "Invoice Rejected",
        action === "accept" ? "success" : "error",
      );
      refetch();
    } catch (err) {
      console.error(err);
      showDialog("Failed to approve invoice.", "Action Failed", "error");
    }
  };

  const handleBulkApproveReject = async (action) => {
    try {
      const currentRows = (Array.isArray(vendorinvoicedata)
        ? vendorinvoicedata
        : []
      ).map(getInvoiceRow);

      if (action === "accept") {
        const selectedSet = new Set(selectedInvoices);
        const blocked = currentRows.filter(
          (inv) => selectedSet.has(inv.invoice_token) && isInvoiceOverallRejected(inv)
        );
        if (blocked.length > 0) {
          showDialog(
            "Some selected invoices are already rejected and cannot be approved.",
            "Bulk Action Blocked",
            "error",
          );
          return;
        }
      }

      const payload = {
        invoice_tokens: selectedInvoices,
        action,
        approver_token: approverToken,
        ...(loginData?.user?.designation === "Account Payable" && {
          is_account: true,
        }),
        ...(loginData?.user?.designation === "Business Finance" && {
          is_business: true,
        }),
      };
      await axios.post(API_ENDPOINT.bulk_approve_vendor_invoices, payload);
      showDialog(
        `Selected invoices have been ${
          action === "accept" ? "approved" : "rejected"
        }.`,
        "Bulk Action Complete",
        action === "accept" ? "success" : "error",
      );
      setSelectedInvoices([]);
      refetch();
    } catch (err) {
      console.error("Bulk action failed", err);
      showDialog("Failed to process bulk action.", "Bulk Action Failed", "error");
    }
  };
  const allInvoiceRows = (Array.isArray(vendorinvoicedata) ? vendorinvoicedata : []).map(getInvoiceRow);

  const filteredData = allInvoiceRows.filter((item) => {
    const payeeStatus =
      item.finance_approved === 1
        ? "Approved"
        : item.finance_approved === 2
          ? "Awaiting"
          : "Cancelled";

    const vendorState = getVendorMerchantState(item);
    const vendorStatus =
      vendorState === "APPROVED"
        ? "Approved"
        : vendorState === "PENDING"
          ? "Pending"
          : "Cancelled";

    const matchesSearch =
      item.vendor_name?.toLowerCase().includes(searchText.toLowerCase()) ||
      item.amount?.toString().includes(searchText);

    const matchesPayee =
      payeeStatusFilter === "All" || payeeStatus === payeeStatusFilter;

    const matchesVendor =
      vendorStatusFilter === "All" || vendorStatus === vendorStatusFilter;

    return matchesSearch && matchesPayee && matchesVendor;
  });

  return (
    <div style={{ margin: "1rem" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <HeaderTitle titleText="Invoice Approval Status" />

        <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
          {/* Payee Status Filter */}
          <select
            value={payeeStatusFilter}
            onChange={(e) => setPayeeStatusFilter(e.target.value)}
            style={{
              padding: "10px",
              fontSize: "14px",
              borderRadius: "6px",
              border: "1px solid #ccc",
              outline: "none",
              width: "150px",
              fontFamily: "gilroyMedium",
            }}
          >
            <option value="All">Payee: All</option>
            <option value="Approved">Payee: Approved</option>
            <option value="Pending">Payee: Awaiting</option>
            <option value="Cancelled">Payee: Rejected</option>
          </select>

          {/* Vendor / Merchant Status Filter */}
          <select
            value={vendorStatusFilter}
            onChange={(e) => setVendorStatusFilter(e.target.value)}
            style={{
              padding: "10px",
              fontSize: "14px",
              borderRadius: "6px",
              border: "1px solid #ccc",
              outline: "none",
              width: "180px",
              fontFamily: "gilroyMedium",
            }}
          >
            <option value="All">Supplier: All</option>
            <option value="Approved">Supplier: Approved</option>
            <option value="Pending">Supplier: Awaiting</option>
            <option value="Cancelled">Supplier: Rejected</option>
          </select>

          <SearchBar
            value={searchText}
            onChange={(value) => setSearchText(value)}
          />
        </div>
      </div>

      {selectedInvoices.length > 0 && (
        <div
          style={{
            marginBottom: "1rem",
            display: "flex",
            gap: "10px",
            fontFamily: "gilroyBold",
          }}
        >
          <Button
            sx={{ fontFamily: "gilroyBold", fontSize: "12px" }}
            variant="contained"
            color="success"
            onClick={() => handleBulkApproveReject("accept")}
          >
            Approve Requests
          </Button>
          <Button
            sx={{ fontFamily: "gilroyBold", fontSize: "12px" }}
            variant="outlined"
            color="error"
            onClick={() => handleBulkApproveReject("reject")}
          >
            Reject Requests
          </Button>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", marginTop: "2rem" }}>
          <CircularProgress />
        </div>
      ) : (
        <TableContainer style={{ background: "#fff" }}>
          <Table>
            <TableHead
              sx={{
                "& .MuiTableCell-root": {
                  backgroundColor: "#e0e9ee !important",
                  fontSize: "16px !important",
                  fontFamily: "gilroyBold",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableRow>
                <TableCell padding="checkbox">
                  <input
                    type="checkbox"
                    checked={
      allInvoiceRows.length > 0 &&
      (() => {
                        const eligible = allInvoiceRows.filter(isSelectableForCurrentRole);
                        return (
                          eligible.length > 0 &&
                          eligible.every((item) =>
                            selectedInvoices.includes(item.invoice_token),
                          )
                        );
                      })()
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        const newTokens = allInvoiceRows
                          .filter(isSelectableForCurrentRole)
                          .map((item) => item.invoice_token);
                        const updated = Array.from(
                          new Set([...selectedInvoices, ...newTokens]),
                        );
                        setSelectedInvoices(updated);
                      } else {
                        const filtered = selectedInvoices.filter(
                          (token) =>
                            !allInvoiceRows.some(
                              (item) => item.invoice_token === token,
                            ),
                        );
                        setSelectedInvoices(filtered);
                      }
                    }}
                    disabled={
                      allInvoiceRows.filter(isSelectableForCurrentRole).length === 0
                    }
                  />
                </TableCell>
                <TableCell>Supplier / Merchant Name</TableCell>
                <TableCell>Amount (₹)</TableCell>
                <TableCell>Invoice</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Payee's Status</TableCell>
                <TableCell>Supplier / Merchant Status</TableCell>
                <TableCell
                  sortDirection={sortConfig.key === "created_on" ? sortConfig.direction : false}
                  sx={{ cursor: "pointer" }}
                >
                  <TableSortLabel
                    active={sortConfig.key === "created_on"}
                    direction={sortConfig.key === "created_on" ? sortConfig.direction : "asc"}
                    onClick={() => handleSort("created_on")}
                  >
                    Raised On
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              sx={{
                "& .MuiTableCell-root": {
                  fontFamily: "gilroyMedium !important",
                  fontSize: "14px !important",
                },
              }}
            >
              {filteredData.map((items) => (
                <TableRow key={items.invoice_token}>
                  <TableCell padding="checkbox">
                    <input
                      type="checkbox"
                      checked={selectedInvoices.includes(items.invoice_token)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedInvoices([
                            ...selectedInvoices,
                            items.invoice_token,
                          ]);
                        } else {
                          setSelectedInvoices(
                            selectedInvoices.filter(
                              (token) => token !== items.invoice_token,
                            ),
                          );
                        }
                      }}
                      disabled={
                        !selectedInvoices.includes(items.invoice_token) &&
                        !isSelectableForCurrentRole(items)
                      }
                    />
                  </TableCell>
                  <TableCell style={{ fontSize: "14px" }}>
                    {items.vendor_name}
                  </TableCell>
                  <TableCell style={{ fontSize: "14px" }}>
                    ₹ {items.amount?.toLocaleString("en-IN")}
                  </TableCell>
                  <TableCell style={{ fontSize: "14px", whiteSpace: "nowrap" }}>
                    <DownloadForOffline
                      fontSize="medium"
                      style={{
                        cursor: "pointer",
                        marginRight: 8,
                        fontSize: "25px",
                      }}
                      onClick={() => downloadPDF(items)}
                    />
                    <Preview
                      fontSize="medium"
                      style={{ cursor: "pointer", fontSize: "25px" }}
                      onClick={() => openPDFInNewTab(items)}
                    />
                  </TableCell>
                  <TableCell style={{ fontSize: "15px", whiteSpace: "nowrap" }}>
                    {loginData?.user?.designation === "Account Payable" && (
                      <>
                        {isInvoiceOverallRejected(items) ? (
                          <Button
                            variant="outlined"
                            disabled
                            color="error"
                            sx={{ fontFamily: "gilroyBold", fontSize: "13px" }}
                          >
                            Rejected
                          </Button>
                        ) : items.account_approved === 1 ? (
                          <Button
                            variant="contained"
                            disabled
                            sx={{ fontFamily: "gilroyBold", fontSize:"13px" }}
                          >
                            Approved
                          </Button>
                        ) : items.account_approved === 3 ? (
                          <Button
                            variant="outlined"
                            disabled
                            color="error"
                            sx={{ fontFamily: "gilroyBold",   fontSize:"13px" }}
                          >
                            Rejected
                          </Button>
                        ) : (
                          <>
                            <Tooltip
                              title={
                                isVendorMerchantApproved(items)
                                  ? ""
                                  : "Approve is enabled after Supplier / Merchant status becomes Approved."
                              }
                              arrow
                              disableHoverListener={isVendorMerchantApproved(items)}
                              classes={{ tooltip: classes.gilroyTooltip }}
                            >
                              <span style={{ display: "inline-block", marginRight: "8px" }}>
                                <Button
                                  variant="contained"
                                  disabled={!isVendorMerchantApproved(items)}
                                  onClick={() =>
                                    handleApprove(
                                      items,
                                      "accept",
                                      "account",
                                    )
                                  }
                                  sx={{ fontFamily: "gilroyBold", fontSize:"13px" }}
                                >
                                  Approve Request
                                </Button>
                              </span>
                            </Tooltip>

                            <Tooltip
                              title={
                                isVendorMerchantApproved(items)
                                  ? ""
                                  : "Reject is enabled after Supplier / Merchant status becomes Approved."
                              }
                              arrow
                              disableHoverListener={isVendorMerchantApproved(items)}
                              classes={{ tooltip: classes.gilroyTooltip }}
                            >
                              <span style={{ display: "inline-block" }}>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  disabled={!isVendorMerchantApproved(items)}
                                  onClick={() =>
                                    handleApprove(
                                      items,
                                      "reject",
                                      "account",
                                    )
                                  }
                                  sx={{ fontFamily: "gilroyBold", fontSize:"13px" }}
                                >
                                  Reject Request
                                </Button>
                              </span>
                            </Tooltip>
                          </>
                        )}
                      </>
                    )}

                    {loginData?.user?.designation === "Business Finance" && (
                      <>
                        {isInvoiceOverallRejected(items) ? (
                          <Button
                            variant="outlined"
                            disabled
                            color="error"
                            sx={{ fontFamily: "gilroyBold", fontSize: "13px" }}
                          >
                            Rejected
                          </Button>
                        ) : items.finance_approved === 1 ? (
                          <Button
                            variant="contained"
                            disabled
                            sx={{ fontFamily: "gilroyBold", fontSize:"13px" }}
                          >
                            Approved
                          </Button>
                        ) : items.finance_approved === 3 ? (
                          <Button
                            variant="outlined"
                            disabled
                            color="error"
                            sx={{ fontFamily: "gilroyBold",fontSize:"13px"}}
                          >
                            Rejected
                          </Button>
                        ) : (
                          <>
                            <Button
                              variant="contained"
                              onClick={() =>
                                handleApprove(
                                  items,
                                  "accept",
                                  "business",
                                )
                              }
                              style={{
                                marginRight: "8px",
                                fontFamily: "gilroyBold",
                                
                                fontSize:"13px"
                              }}
                            >
                              Approve Request
                            </Button>

                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() =>
                                handleApprove(
                                  items,
                                  "reject",
                                  "business",
                                )
                              }
                              sx={{ fontFamily: "gilroyBold",  fontSize:"13px" }}
                            >
                              Reject Request
                            </Button>
                          </>
                        )}
                      </>
                    )}
                  </TableCell>

                  {loginData?.user?.designation === "Business Finance" && (
                    <TableCell
                      className={classes.status}
                      style={{ fontSize: "15px" }}
                    >
                      {items.finance_approved === 1 ? (
                        <span
                          style={{
                            color: "green",

                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            fontSize: "14px",
                          }}
                        >
                          <VerifiedIcon /> Approved
                        </span>
                      ) : items.finance_approved === 2 ? (
                        <span
                          style={{
                            color: "orange",

                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            fontSize: "14px",
                          }}
                        >
                          <HourglassBottom /> Awaiting
                        </span>
                      ) : (
                        <span
                          style={{
                            color: "red",

                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            fontSize: "14px",
                          }}
                        >
                          <DisabledByDefault /> Rejected
                        </span>
                      )}
                    </TableCell>
                  )}

                  {loginData?.user?.designation === "Account Payable" && (
                    <TableCell className={classes.status}>
                      {items.account_approved === 1 ? (
                        <span
                          style={{
                            color: "green",

                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            fontSize: "14px",
                          }}
                        >
                          <VerifiedIcon /> Approved
                        </span>
                      ) : items.account_approved === 2 ? (
                        <span
                          style={{
                            color: "orange",

                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            fontSize: "14px",
                          }}
                        >
                          <HourglassBottom /> Awaiting
                        </span>
                      ) : (
                        <span
                          style={{
                            color: "red",

                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            fontSize: "14px",
                          }}
                        >
                          <DisabledByDefault /> Rejected
                        </span>
                      )}
                    </TableCell>
                  )}

                  <TableCell className={classes.status}>
                    {getVendorMerchantState(items) === "APPROVED" ? (
                      <span
                        style={{
                          color: "green",

                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          fontSize: "14px",
                        }}
                      >
                        <VerifiedIcon /> Approved
                      </span>
                    ) : getVendorMerchantState(items) === "PENDING" ? (
                      <span
                        style={{
                          color: "orange",

                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          fontSize: "14px",
                        }}
                      >
                        <HourglassBottom /> Awaiting
                      </span>
                    ) : (
                      <span
                        style={{
                          color: "red",

                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          fontSize: "14px",
                        }}
                      >
                        <DisabledByDefault /> Rejected
                      </span>
                    )}
                  </TableCell>
                  <TableCell style={{ fontSize: "15px" ,whiteSpace:"noWrap"}}>
                    {new Date(items.created_on).toLocaleString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            sx={{
              "& .MuiTablePagination-toolbar": { fontSize: "14px" },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                { fontSize: "14px" },
              "& .MuiInputBase-root": { fontSize: "14px" },
            }}
          />
        </TableContainer>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        PaperProps={{
          sx: {
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 2,
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{ fontWeight: 600, fontSize: "20px", fontFamily: "gilroyBold" }}
        >
          {dialogState.title}
        </DialogTitle>
        <DialogContent>
          <Typography fontFamily="gilroyMedium" sx={{ fontSize: "18px" }}>
            {dialogState.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setDialogOpen(false)}
            sx={{ fontSize: "14px" }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ApproverInvoice;
